import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Benchmark mode: blind model-vs-model votes for the hermes3000 Writing Benchmark.
 * Reached at /benchmark?key=<judge key>; the key is kept in localStorage and removed from the
 * address bar. Model names never reach this page — they appear only in the server's export.
 * German only on purpose: the judged texts are German and the judges are named people.
 */

type Choice = 'left' | 'right' | 'tie';

interface BenchPair {
  id: number;
  taskTitle: string;
  prompt: string;
  leftText: string;
  rightText: string;
}

interface NextResponse {
  done: boolean;
  judge: string;
  total: number;
  voted: number;
  pair?: BenchPair;
}

const KEY_STORAGE = 'arena-bench-key';

function readKey(): string {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('key');
  if (fromUrl) {
    try {
      localStorage.setItem(KEY_STORAGE, fromUrl);
    } catch {
      // storage blocked: the key still works for this page view
    }
    params.delete('key');
    const rest = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    return fromUrl;
  }
  try {
    return localStorage.getItem(KEY_STORAGE) || '';
  } catch {
    return '';
  }
}

const words = (s: string) => (s.match(/\S+/g) || []).length;

export default function BenchmarkJudge() {
  const [key] = useState(readKey);
  const [runId] = useState(() => new URLSearchParams(window.location.search).get('run') || '');
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'done'; judge: string; total: number; voted: number }
    | { kind: 'pair'; judge: string; total: number; voted: number; pair: BenchPair }
  >({ kind: 'loading' });
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const startedAt = useRef(Date.now());

  const load = useCallback(async () => {
    if (!key) {
      setState({
        kind: 'error',
        message: 'Kein Zugangsschlüssel. Bitte den Link mit ?key=… öffnen.',
      });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const res = await fetch(
        `/api/bench/next${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`,
        {
          headers: { 'x-bench-key': key },
        }
      );
      if (res.status === 401) {
        setState({ kind: 'error', message: 'Der Zugangsschlüssel ist ungültig.' });
        return;
      }
      const data = (await res.json()) as NextResponse;
      if (data.done || !data.pair) {
        setState({ kind: 'done', judge: data.judge, total: data.total, voted: data.voted });
      } else {
        setState({
          kind: 'pair',
          judge: data.judge,
          total: data.total,
          voted: data.voted,
          pair: data.pair,
        });
        setReason('');
        startedAt.current = Date.now();
        window.scrollTo({ top: 0 });
      }
    } catch {
      setState({ kind: 'error', message: 'Der Server ist nicht erreichbar.' });
    }
  }, [key, runId]);

  useEffect(() => {
    load();
  }, [load]);

  const vote = useCallback(
    async (choice: Choice) => {
      if (state.kind !== 'pair' || sending) return;
      setSending(true);
      try {
        const res = await fetch('/api/bench/votes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-bench-key': key },
          body: JSON.stringify({
            pairId: state.pair.id,
            choice,
            reason,
            timeSpentMs: Date.now() - startedAt.current,
          }),
        });
        if (!res.ok) throw new Error(String(res.status));
        await load();
      } catch {
        setState({ kind: 'error', message: 'Die Stimme konnte nicht gespeichert werden.' });
      } finally {
        setSending(false);
      }
    },
    [state, sending, key, reason, load]
  );

  // Arrow keys vote, except while typing a reason.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft') vote('left');
      else if (e.key === 'ArrowRight') vote('right');
      else if (e.key === 'ArrowDown') vote('tie');
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [vote]);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p className="font-serif text-stone-600">{state.message}</p>
      </div>
    );
  }

  if (state.kind === 'done') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <h1 className="font-display text-3xl text-stone-800 mb-3">Alle Paare bewertet</h1>
        <p className="font-serif text-stone-600">
          Danke, {state.judge}. {state.total} Paare sind bewertet.
        </p>
      </div>
    );
  }

  const { pair, voted, total, judge } = state;
  return (
    <div className="min-h-screen px-4 py-6 md:py-10">
      <div className="w-full max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-2 text-sm font-sans text-stone-400">
          <span>Writing Benchmark · blind</span>
          <span>
            {judge} · {voted} von {total} bewertet
          </span>
        </div>
        <div className="w-full bg-stone-200 rounded-full h-1 mb-6">
          <div
            className="bg-stone-400 h-1 rounded-full transition-all duration-500"
            style={{ width: `${total ? (voted / total) * 100 : 0}%` }}
          />
        </div>

        <h1 className="font-display text-2xl text-stone-800 mb-3">{pair.taskTitle}</h1>
        <details className="mb-6 rounded-lg border border-stone-200 bg-stone-50 p-4" open>
          <summary className="cursor-pointer font-sans text-sm text-stone-500">Aufgabe</summary>
          <p className="mt-3 font-serif text-sm text-stone-700 whitespace-pre-line leading-relaxed">
            {pair.prompt}
          </p>
        </details>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {(['left', 'right'] as const).map((side) => {
            const text = side === 'left' ? pair.leftText : pair.rightText;
            return (
              <article
                key={side}
                className="rounded-xl border border-stone-200 bg-white p-5 md:p-7"
              >
                <div className="flex justify-between mb-3 text-[11px] font-sans uppercase tracking-widest text-stone-300">
                  <span>{side === 'left' ? 'Links' : 'Rechts'}</span>
                  <span>{words(text)} Wörter</span>
                </div>
                <p className="font-serif text-stone-700 leading-[1.8] whitespace-pre-line break-words">
                  {text}
                </p>
              </article>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-stone-50/95 backdrop-blur border-t border-stone-200 -mx-4 px-4 py-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-3 md:items-center">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Begründung (optional)"
              rows={1}
              className="flex-1 rounded-lg border border-stone-200 bg-white px-3 py-2 font-sans text-sm text-stone-700"
            />
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => vote('left')}
                disabled={sending}
                className="rounded-lg border border-stone-300 bg-white px-4 py-2 font-sans text-sm text-stone-700 hover:border-stone-500 disabled:opacity-50"
              >
                ← Links besser
              </button>
              <button
                onClick={() => vote('tie')}
                disabled={sending}
                className="rounded-lg border border-stone-300 bg-white px-4 py-2 font-sans text-sm text-stone-700 hover:border-stone-500 disabled:opacity-50"
              >
                ↓ Gleichwertig
              </button>
              <button
                onClick={() => vote('right')}
                disabled={sending}
                className="rounded-lg border border-stone-300 bg-white px-4 py-2 font-sans text-sm text-stone-700 hover:border-stone-500 disabled:opacity-50"
              >
                Rechts besser →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
